import { isEnvTrue } from "src/services/env";

import { withBackgroundRoutePrefetchImport } from "./idleRoutePrefetchReloadGuard.js";

const IDLE_ROUTE_PREFETCH_QUERY_PARAM = "idleRoutePrefetch";

/**
 * Prefetch is on by default. Disable with ?idleRoutePrefetch=0 or false.
 * Enable explicitly with ?idleRoutePrefetch=1 or true (same as default).
 */
export function isIdleRoutePrefetchEnabledByUrl(
  search = typeof window !== "undefined" ? window.location.search : ""
) {
  const value = new URLSearchParams(search).get(
    IDLE_ROUTE_PREFETCH_QUERY_PARAM
  );
  if (value === null) return true;
  return isEnvTrue(value);
}

/** @param {string | null | undefined} paramValue from URLSearchParams.get */
export function isIdleRoutePrefetchEnabledByParam(paramValue) {
  if (paramValue === null || paramValue === undefined) return true;
  return isEnvTrue(paramValue);
}

function readNetworkInformation() {
  return navigator.connection;
}

/** Skip prefetch on save-data or very slow links. */
export function shouldSkipIdleRoutePrefetchForNetwork(connection) {
  const resolved = connection ?? readNetworkInformation();
  if (!resolved) return false;
  if (resolved.saveData) return true;
  const type = resolved.effectiveType?.toLowerCase();
  return type === "slow-2g" || type === "2g";
}

const DEFAULT_INITIAL_DELAY_MS = 3000;
const DEFAULT_IDLE_TIMEOUT_MS = 8000;
const DEFAULT_GAP_MS = 250;
const RIC_FALLBACK_DELAY_MS = 2000;

function isDocumentHidden() {
  return (
    typeof document !== "undefined" && document.visibilityState === "hidden"
  );
}

/** Safari and some WebViews lack requestIdleCallback; defer work instead of running immediately. */
export function scheduleIdleTask(callback, idleOptions = {}) {
  if (typeof window.requestIdleCallback === "function") {
    return window.requestIdleCallback(callback, idleOptions);
  }
  const timeoutMs = idleOptions.timeout ?? DEFAULT_IDLE_TIMEOUT_MS;
  const delayMs = Math.min(timeoutMs, RIC_FALLBACK_DELAY_MS);
  return setTimeout(
    () =>
      callback({
        didTimeout: true,
        timeRemaining: () => 0,
      }),
    delayMs
  );
}

export function cancelIdleTask(handle) {
  if (handle === undefined) return;
  if (typeof window.cancelIdleCallback === "function") {
    window.cancelIdleCallback(handle);
  } else {
    clearTimeout(handle);
  }
}

async function prefetchLoader(loader) {
  try {
    await withBackgroundRoutePrefetchImport(loader);
    return true;
  } catch {
    return false;
  }
}

export function startIdleRouteChunkPrefetch(options = {}) {
  const loaders = options.loaders;
  if (!loaders?.length) {
    return () => {};
  }
  const scheduleIdle = options.scheduleIdle ?? scheduleIdleTask;
  const cancelIdle = options.cancelIdle ?? cancelIdleTask;
  const setTimeoutFn = options.setTimeoutFn ?? setTimeout;
  const clearTimeoutFn = options.clearTimeoutFn ?? clearTimeout;
  const documentRef = options.documentRef ?? document;
  const initialDelayMs = options.initialDelayMs ?? DEFAULT_INITIAL_DELAY_MS;
  const idleTimeoutMs = options.idleTimeoutMs ?? DEFAULT_IDLE_TIMEOUT_MS;
  const gapMs = options.gapMs ?? DEFAULT_GAP_MS;

  let cancelled = false;
  let delayTimer;
  let gapTimer;
  let idleHandle;
  let visibilityListener;
  let nextIndex = 0;
  let loaderInFlight = false;
  let queueStopped = false;

  const cancel = () => {
    cancelled = true;
    if (delayTimer !== undefined) clearTimeoutFn(delayTimer);
    if (gapTimer !== undefined) clearTimeoutFn(gapTimer);
    if (idleHandle !== undefined) cancelIdle(idleHandle);
    if (visibilityListener) {
      documentRef.removeEventListener("visibilitychange", visibilityListener);
      visibilityListener = undefined;
    }
  };

  const canScheduleMore = () =>
    !cancelled &&
    !queueStopped &&
    !loaderInFlight &&
    delayTimer === undefined &&
    idleHandle === undefined &&
    gapTimer === undefined &&
    nextIndex < loaders.length &&
    !isDocumentHidden();

  const prefetchAtIndex = (index) => {
    if (cancelled || queueStopped || index >= loaders.length) return;
    if (isDocumentHidden()) return;
    if (loaderInFlight || idleHandle !== undefined) return;

    idleHandle = scheduleIdle(
      () => {
        idleHandle = undefined;
        if (cancelled || queueStopped || isDocumentHidden()) return;

        loaderInFlight = true;
        void prefetchLoader(loaders[index]).then((ok) => {
          loaderInFlight = false;
          if (cancelled) return;
          if (!ok) {
            queueStopped = true;
            return;
          }
          nextIndex = index + 1;
          if (nextIndex >= loaders.length) return;
          gapTimer = setTimeoutFn(() => {
            gapTimer = undefined;
            prefetchAtIndex(nextIndex);
          }, gapMs);
        });
      },
      { timeout: idleTimeoutMs }
    );
  };

  visibilityListener = () => {
    if (!canScheduleMore()) return;
    prefetchAtIndex(nextIndex);
  };
  documentRef.addEventListener("visibilitychange", visibilityListener);

  delayTimer = setTimeoutFn(() => {
    delayTimer = undefined;
    if (cancelled) return;
    prefetchAtIndex(0);
  }, initialDelayMs);

  return cancel;
}
